import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useForm } from 'react-hook-form';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  BillingKeys,
  setInitialPrimaryBillingContact,
  updateHubSpotContact,
} from '../../../api/billing-api';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  BillingInformationModel,
  HubSpotCompany,
  HubSpotContact,
} from '../../../api/billing-types';
import HubspotContactInfoForm from './hubspot-contact-info-form';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import HubspotShippingAddressForm from './hubspot-shipping-address-form';
import HubspotContactAddressForm from './hubspot-contact-address-form';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
interface ValidateHubspotContactFormProps {
  open: boolean;
  handleClose: () => void;
  billingInformationModel: BillingInformationModel;
  hubSpotCompany: HubSpotCompany;
  currentRoute?: string;
  setOpenAddPaymentMethodDialog?: Dispatch<SetStateAction<boolean>>;
}

export default function ValidateHubspotContactDialog({
  open,
  handleClose,
  billingInformationModel,
  hubSpotCompany,
  currentRoute,
  setOpenAddPaymentMethodDialog,
}: ValidateHubspotContactFormProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const { qbInstance, billingRoles } = useUserSettings();
  const navigate = useNavigate();

  const billingAddress = billingInformationModel.billingAddress;
  const shippingAddress = billingInformationModel.shippingAddress;
  const initialAddressesMatch =
    billingAddress?.streetAddress === shippingAddress?.streetAddress &&
    billingAddress?.streetAddress2 === shippingAddress?.streetAddress2 &&
    billingAddress?.city === shippingAddress?.city &&
    billingAddress?.state === shippingAddress?.state &&
    billingAddress?.country === shippingAddress?.country &&
    billingAddress?.zipCode === shippingAddress?.zipCode;

  const [matchBillingAddress, setMatchBillingAddress] = useState(
    initialAddressesMatch
  );

  const getAnalyticsSection = (currentRoute: string | undefined) => {
    const addLicenseRoute = `/${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}/${pagesConfig.addLicense.link}`;
    const section =
      currentRoute && currentRoute.startsWith(addLicenseRoute.split('/:')[0])
        ? ModuleList.modules.billing.sections.subscriptions
        : ModuleList.modules.billing.sections.payments;
    return section;
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm<Partial<HubSpotContact>>({
    defaultValues: {
      id: billingInformationModel.hubSpotContactId ?? 0,
      properties: {
        primary_billing_contact_: true,
        firstName: billingInformationModel.firstName || '',
        lastName: billingInformationModel.lastName || '',
        phone: billingInformationModel.shippingAddress?.phone || '',
        email: billingInformationModel.emailAddress || '',

        // billing address fields
        address: billingInformationModel.billingAddress?.streetAddress || '',
        street_address_2:
          billingInformationModel.billingAddress?.streetAddress2 || '',
        city: billingInformationModel.billingAddress?.city || '',
        state: billingInformationModel.billingAddress?.state || '',
        country: billingInformationModel.billingAddress?.country || 'US',
        zip: billingInformationModel.billingAddress?.zipCode || '',

        // shipping address fields
        street_address_1__shipping_:
          billingInformationModel.shippingAddress?.streetAddress || '',
        street_address_2__shipping_:
          billingInformationModel.shippingAddress?.streetAddress2 || '',
        city__shipping_: billingInformationModel.shippingAddress?.city || '',
        state__shipping_: billingInformationModel.shippingAddress?.state || '',
        country__shipping_:
          billingInformationModel.shippingAddress?.country || 'US',
        postal_code__shipping_:
          billingInformationModel.shippingAddress?.zipCode || '',
      },
    },
  });

  useEffect(() => {
    // Delaying validation slightly to ensure form is fully rendered
    const validateForm = async () => {
      setTimeout(async () => {
        await trigger(); // Trigger validation after component is fully rendered
      }, 0);
    };
    validateForm();
  }, [trigger, billingInformationModel]);

  const isAbleToSetPrimaryBillingContact = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canChangePrimaryContact.value
  );

  const hasPrimaryBillingContact =
    billingInformationModel.customerInfoModel &&
    billingInformationModel.hubSpotContactId;

  const onSubmit = (data: Partial<HubSpotContact>) => {
    if (!data.properties) {
      return;
    }

    if (matchBillingAddress) {
      data.properties.street_address_1__shipping_ = data.properties.address;
      data.properties.street_address_2__shipping_ =
        data.properties.street_address_2;
      data.properties.city__shipping_ = data.properties.city;
      data.properties.state__shipping_ = data.properties.state;
      data.properties.country__shipping_ = data.properties.country;
      data.properties.postal_code__shipping_ = data.properties.zip;
    }

    if (hasPrimaryBillingContact) {
      mutate(data);
    } else {
      newCustomerMutate(data);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<HubSpotContact>) =>
      updateHubSpotContact(data, ''),
    onSuccess: (_, variables) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Billing contact'),
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.HubSpotContacts],
      });

      queryClient.setQueryData<BillingInformationModel>(
        [BillingKeys.CustomerBillingInformation],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            firstName: variables.properties?.firstName ?? oldData.firstName,
            lastName: variables.properties?.lastName ?? oldData.lastName,
            emailAddress: variables.properties?.email ?? oldData.emailAddress,
            billingAddress: {
              ...oldData.billingAddress,
              streetAddress:
                variables.properties?.address ??
                oldData.billingAddress?.streetAddress,
              streetAddress2:
                variables.properties?.street_address_2 ??
                oldData.billingAddress?.streetAddress2,
              city: variables.properties?.city ?? oldData.billingAddress?.city,
              state:
                variables.properties?.state ?? oldData.billingAddress?.state,
              country:
                variables.properties?.country ??
                oldData.billingAddress?.country,
              zipCode:
                variables.properties?.zip ?? oldData.billingAddress?.zipCode,
            },
          };
        }
      );

      handleClose();

      if (currentRoute) {
        navigate(currentRoute);
      } else if (setOpenAddPaymentMethodDialog) {
        setOpenAddPaymentMethodDialog(true);
      }
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('billing contact'),
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.subscriptions.update_magento_contact, {
        module: ModuleList.modules.billing.name,
        section: getAnalyticsSection(currentRoute),
      });
    },
  });

  const { mutate: newCustomerMutate, isPending: newCustomerAdding } =
    useMutation({
      mutationFn: (data: Partial<HubSpotContact>) =>
        setInitialPrimaryBillingContact(data, hubSpotCompany.id, qbInstance),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.Success.Created('Primary billing contact'),
        });
        handleClose();
        queryClient.invalidateQueries({
          queryKey: [BillingKeys.CustomerBillingInformation],
        });

        if (currentRoute) {
          navigate(currentRoute);
        } else if (setOpenAddPaymentMethodDialog) {
          setOpenAddPaymentMethodDialog(true);
        }
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Creating(
            'a primary billing contact'
          ),
        });
      },
      onSettled: () => {
        createEvent(analytics.billing.subscriptions.create_magento_contact, {
          module: ModuleList.modules.billing.name,
          section: getAnalyticsSection(currentRoute),
        });
      },
    });

  if (!isAbleToSetPrimaryBillingContact && !hasPrimaryBillingContact) {
    return (
      <SharedDialog
        open={open}
        handleClose={handleClose}
        maxWidth="lg"
        title="Update Primary Billing Contact"
        dialogContent={
          <Box sx={{ maxHeight: 620, overflow: 'auto' }}>
            <Alert severity="error">
              {Messages.Errors.Permission.Acting(
                BillingRoles.canChangePrimaryContact.name
              )}
            </Alert>
          </Box>
        }
        primaryButton={{
          loading: false,
          action: handleClose,
          disabled: false,
          title: 'Close',
        }}
      />
    );
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      maxWidth="lg"
      title={
        hasPrimaryBillingContact
          ? 'Update Primary Billing Contact'
          : 'Set Primary Billing Contact'
      }
      dialogContent={
        <Box sx={{ maxHeight: 620, overflow: 'auto' }}>
          {hasPrimaryBillingContact ? (
            <Typography sx={{ mb: 3 }}>
              We've detected your primary billing contact is missing some
              information, please fill out the following in order to add payment
              methods.
            </Typography>
          ) : (
            <Typography sx={{ mb: 3 }}>
              Please fill out the following form to set a Primary Billing
              Contact for any additional license orders placed for your domain.
              This contact will be where all invoices for additional license
              orders will be placed.
            </Typography>
          )}
          <HubspotContactInfoForm
            control={control}
            errors={errors}
            trigger={trigger}
          />

          <Divider sx={{ my: 2 }} />

          <HubspotContactAddressForm
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            trigger={trigger}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={matchBillingAddress}
                onClick={() => {
                  if (matchBillingAddress) {
                    setValue('properties.street_address_1__shipping_', '');
                    setValue('properties.street_address_2__shipping_', '');
                    setValue('properties.city__shipping_', '');
                    setValue('properties.state__shipping_', '');
                    setValue('properties.country__shipping_', '');
                    setValue('properties.postal_code__shipping_', '');
                    setMatchBillingAddress(false);
                  } else {
                    setValue(
                      'properties.street_address_1__shipping_',
                      getValues('properties.address')
                    );
                    setValue(
                      'properties.street_address_2__shipping_',
                      getValues('properties.street_address_2')
                    );
                    setValue(
                      'properties.city__shipping_',
                      getValues('properties.city')
                    );
                    setValue(
                      'properties.state__shipping_',
                      getValues('properties.state')
                    );
                    setValue(
                      'properties.country__shipping_',
                      getValues('properties.country')
                    );
                    setValue(
                      'properties.postal_code__shipping_',
                      getValues('properties.zip')
                    );

                    setMatchBillingAddress(true);
                  }
                }}
              />
            }
            label="Shipping address matches billing address"
          />

          {!matchBillingAddress && (
            <>
              <Divider sx={{ my: 2 }} />
              <HubspotShippingAddressForm
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                trigger={trigger}
              />
            </>
          )}
        </Box>
      }
      primaryButton={{
        loading: isPending || newCustomerAdding,
        action: handleSubmit(onSubmit),
        disabled: !isDirty,
        title: 'Update Billing Contact',
      }}
    />
  );
}
